import { useNavigate } from 'react-router-dom';
import { MoreHorizontal, Pencil, Trash2, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui';
import { formatCents } from '@/lib/utils';
import type { Service } from '@/types/service';

interface ServiceCardProps {
  service: Service;
  onEdit?: (service: Service) => void;
  onDelete?: (service: Service) => void;
}

/**
 * Card component displaying a service summary.
 * Shows key info: name, amount, interval, status.
 * Provides actions: view, edit, delete.
 */
export function ServiceCard({ service, onEdit, onDelete }: ServiceCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('services');

  const handleView = () => {
    navigate(`/services/${service.serviceId}`, {
      state: { merchantId: service.merchantId },
    });
  };

  return (
    <Card className="relative">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle className="text-base font-medium leading-tight">
            {service.description}
          </CardTitle>
          <CardDescription className="font-mono text-xs">{service.serviceId}</CardDescription>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">{t('common:aria.openActions')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleView}>
              <Eye className="mr-2 h-4 w-4" />
              {t('common:label.view')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit?.(service)}>
              <Pencil className="mr-2 h-4 w-4" />
              {t('common:button.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete?.(service)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('common:button.deactivate')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={service.status === 'active' ? 'success' : 'secondary'}>
            {service.status === 'active' ? t('common:status.active') : t('common:status.inactive')}
          </Badge>
          <Badge variant="outline">{formatCents(service.grossAmount)}</Badge>
          <Badge variant="outline">{t(`interval.${service.subscriptionInterval}`)}</Badge>
        </div>
        <div className="mt-3 space-y-1 text-sm text-muted-foreground">
          <div>
            <span className="text-xs text-muted-foreground">{t('card.merchant')} </span>
            <span className="font-mono text-xs">{service.merchantId}</span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">{t('card.gateway')} </span>
            <span className="font-mono text-xs">{service.gatewayServiceId}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
